// =====================================================
// Subscription Product API -> SubscriptionProductController
// Base: /api/subscription-products
// =====================================================

const API = "/api/subscription-products";

// =====================================================
// Subscription Purchase API
// Base: /api/subscription-purchases
// =====================================================

const PURCHASE_API =
  "/api/subscription-purchases";

// Change these if your enum has different values
export const USER_TYPES = ["PARTNER", "TRAINER"];

// =====================================================
// AUTH HEADERS
// =====================================================

const authHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    ...(token
      ? { Authorization: `Bearer ${token}` }
      : {}),
  };
};

// =====================================================
// COMMON REQUEST
// =====================================================

async function request(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

  const text = await res.text();

  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    const message =
      (typeof data === "string" && data) ||
      data?.message ||
      data?.error ||
      `Request failed (${res.status})`;

    throw new Error(message);
  }

  return data;
}

// =====================================================
// PRODUCT ENDPOINTS
// =====================================================

export const createProduct = (payload) =>
  request(API, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const getAllProducts = () =>
  request(API);

export const getActiveProducts = () =>
  request(`${API}/active`);

export const getPartnerPlans = () =>
  request(`${API}/partner`);

export const getProductsByUserType = (type) =>
  request(`${API}/user-type/${type}`);

export const getActiveProductsByUserType = (type) =>
  request(`${API}/user-type/${type}/active`);

export const getProductById = (id) =>
  request(`${API}/${id}`);

export const updateProduct = (id, payload) =>
  request(`${API}/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

export const toggleProductStatus = (id) =>
  request(`${API}/${id}/toggle-status`, {
    method: "PATCH",
  });

export const deleteProduct = (id) =>
  request(`${API}/${id}`, {
    method: "DELETE",
  });

// =====================================================
// PRODUCT FIELD MAPPING
// =====================================================

const num = (v) =>
  v === null ||
  v === undefined ||
  v === ""
    ? null
    : Number(v);

export const normalizeProduct = (p = {}) => ({
  id: p.id,

  name:
    p.name ??
    "Untitled",

  code:
    p.code ??
    "",

  featureCode:
    p.featureCode ??
    p.feature_code ??
    "",

  description:
    p.description ??
    "",

  price:
    Number(p.price ?? 0) || 0,

  duration:
    Number(
      p.validityDays ??
        p.validity_days ??
        0
    ) || 0,

  userType:
    String(
      p.userType ??
        p.user_type ??
        ""
    ),

  active:
    Boolean(
      p.active ??
        p.isActive ??
        p.is_active ??
        false
    ),

  jobPostLimit:
    num(
      p.jobPostLimit ??
        p.job_post_limit
    ),

  jobInviteLimit:
    num(
      p.jobInviteLimit ??
        p.job_invite_limit
    ),

  profileViewLimit:
    num(
      p.profileViewLimit ??
        p.profile_view_limit
    ),

  resumeLimit:
    num(
      p.resumeLimit ??
        p.resume_limit
    ),
});

// =====================================================
// PLAN LIMITS
// =====================================================

const LIMITS = [
  [
    "jobPostLimit",
    "job posts",
    "Jobs",
  ],
  [
    "jobInviteLimit",
    "job invites",
    "Invites",
  ],
  [
    "profileViewLimit",
    "profile views",
    "Views",
  ],
  [
    "resumeLimit",
    "resumes",
    "Resumes",
  ],
];

export const planLimits = (p) =>
  LIMITS
    .filter(
      ([k]) =>
        p[k] !== null &&
        p[k] !== undefined
    )
    .map(
      ([key, label, short]) => ({
        key,
        label,
        short,
        value:
          p[key] < 0
            ? "Unlimited"
            : p[key],
      })
    );

// =====================================================
// PRODUCT PAYLOAD
// =====================================================

export const toCode = (text) =>
  String(text)
    .trim()
    .toUpperCase()
    .replace(
      /[^A-Z0-9]+/g,
      "_"
    )
    .replace(
      /^_|_$/g,
      ""
    );

export const toPayload = (form) => {
  const limit = (key) =>
    form[key] === ""
      ? {}
      : {
          [key]: Number(
            form[key]
          ),
        };

  return {
    name:
      form.name.trim(),

    code:
      form.code.trim() ||
      toCode(form.name),

    featureCode:
      form.featureCode.trim() ||
      `${form.userType}_SUBSCRIPTION`,

    description:
      form.description.trim(),

    price:
      Number(form.price),

    validityDays:
      Number(form.validityDays),

    userType:
      form.userType,

    active:
      form.active,

    isActive:
      form.active,

    ...limit(
      "jobPostLimit"
    ),

    ...limit(
      "jobInviteLimit"
    ),

    ...limit(
      "profileViewLimit"
    ),

    ...limit(
      "resumeLimit"
    ),
  };
};

// =====================================================
// DURATION
// =====================================================

export const formatDuration = (
  days
) => {
  if (!days) return "—";

  if (days % 365 === 0) {
    return `${days / 365} year${
      days / 365 > 1
        ? "s"
        : ""
    }`;
  }

  if (days % 30 === 0) {
    return `${days / 30} month${
      days / 30 > 1
        ? "s"
        : ""
    }`;
  }

  return `${days} days`;
};

// =====================================================
// CURRENT USER
// =====================================================

export const getCurrentUser = () => {
  try {
    const rawUser =
      localStorage.getItem(
        "user"
      );

    if (rawUser) {
      const user =
        JSON.parse(rawUser);

      return {
        ...user,

        id:
          user?.id ??
          user?.userId ??
          user?.user_id ??
          localStorage.getItem(
            "userId"
          ),

        role:
          user?.role ??
          user?.userType ??
          user?.user_type ??
          localStorage.getItem(
            "role"
          ) ??
          localStorage.getItem(
            "userRole"
          ),

        name:
          user?.name ??
          user?.fullName ??
          user?.username ??
          "",

        email:
          user?.email ??
          user?.emailId ??
          "",

        phone:
          user?.phone ??
          user?.phoneNumber ??
          user?.mobile ??
          user?.mobileNumber ??
          "",
      };
    }
  } catch (error) {
    console.error(
      "Unable to read current user",
      error
    );
  }

  return {
    id:
      localStorage.getItem(
        "userId"
      ) ||
      localStorage.getItem(
        "id"
      ) ||
      "",

    role:
      localStorage.getItem(
        "role"
      ) ||
      localStorage.getItem(
        "userRole"
      ) ||
      "",

    name:
      localStorage.getItem(
        "name"
      ) ||
      "",

    email:
      localStorage.getItem(
        "email"
      ) ||
      "",

    phone:
      localStorage.getItem(
        "phone"
      ) ||
      "",
  };
};

// =====================================================
// CURRENT USER TYPE
// =====================================================

export const getCurrentUserType = () => {
  const user =
    getCurrentUser();

  let role = String(
    user?.role || ""
  )
    .replace(
      "ROLE_",
      ""
    )
    .trim()
    .toUpperCase();

  // If your application uses RECRUITER
  // for Partner
  if (
    role === "RECRUITER"
  ) {
    role = "PARTNER";
  }

  if (
    USER_TYPES.includes(
      role
    )
  ) {
    return role;
  }

  return "";
};

// =====================================================
// GST CALCULATION
// =====================================================

export const calculateSubscriptionAmount = (
  price,
  discount = 0,
  gstRate = 18
) => {
  const basePrice =
    Number(price || 0);

  const discountAmount =
    Number(discount || 0);

  const taxableAmount =
    Math.max(
      0,
      basePrice -
        discountAmount
    );

  const gstAmount =
    Number(
      (
        (taxableAmount *
          Number(gstRate)) /
        100
      ).toFixed(2)
    );

  const totalAmount =
    Number(
      (
        taxableAmount +
        gstAmount
      ).toFixed(2)
    );

  return {
    basePrice,
    discountAmount,
    taxableAmount,
    gstRate:
      Number(gstRate),
    gstAmount,
    totalAmount,
  };
};

// =====================================================
// CREATE SUBSCRIPTION PURCHASE
// =====================================================

export const createSubscriptionPurchase =
  async ({
    productId,
    userId,
    userType,
    paymentMethod = "ONLINE",
    discountAmount = 0,
  }) => {
    if (!productId) {
      throw new Error(
        "Subscription product is required."
      );
    }

    if (!userId) {
      throw new Error(
        "User ID is required."
      );
    }

    if (!userType) {
      throw new Error(
        "User type is required."
      );
    }

    const payload = {
      subscriptionProductId:
        Number(productId),

      userId:
        Number(userId),

      userType:
        String(
          userType
        ).toUpperCase(),

      paymentMethod,

      discountAmount:
        Number(
          discountAmount || 0
        ),
    };

    return request(
      PURCHASE_API,
      {
        method: "POST",
        body: JSON.stringify(
          payload
        ),
      }
    );
  };

// =====================================================
// PAYMENT SUCCESS
// =====================================================

export const confirmSubscriptionPayment =
  async ({
    purchaseId,
    transactionId,
    paymentMethod = "ONLINE",
  }) => {
    if (!purchaseId) {
      throw new Error(
        "Purchase ID is required."
      );
    }

    if (!transactionId) {
      throw new Error(
        "Transaction ID is required."
      );
    }

    return request(
      `${PURCHASE_API}/payment-success`,
      {
        method: "POST",
        body: JSON.stringify({
          purchaseId:
            Number(
              purchaseId
            ),

          transactionId:
            String(
              transactionId
            ),

          paymentMethod,
        }),
      }
    );
  };

// =====================================================
// PAYMENT FAILED
// =====================================================

export const failSubscriptionPayment =
  async (purchaseId) => {
    if (!purchaseId) {
      throw new Error(
        "Purchase ID is required."
      );
    }

    return request(
      `${PURCHASE_API}/${purchaseId}/payment-failed`,
      {
        method: "POST",
      }
    );
  };

// =====================================================
// GET PURCHASE BY ID
// =====================================================

export const getSubscriptionPurchase =
  (purchaseId) =>
    request(
      `${PURCHASE_API}/${purchaseId}`
    );

// =====================================================
// GET ALL USER PURCHASES
// =====================================================

export const getUserSubscriptionPurchases =
  (userId) =>
    request(
      `${PURCHASE_API}/user/${userId}`
    );

// =====================================================
// GET ACTIVE SUBSCRIPTION
// =====================================================

export const getActiveSubscription =
  (userId) =>
    request(
      `${PURCHASE_API}/user/${userId}/active`
    );

// =====================================================
// CANCEL SUBSCRIPTION
// =====================================================

export const cancelSubscription =
  (purchaseId) =>
    request(
      `${PURCHASE_API}/${purchaseId}/cancel`,
      {
        method: "PATCH",
      }
    );

// =====================================================
// PURCHASE HELPERS
// =====================================================

export const getPurchaseStatusLabel = (
  status
) => {
  switch (
    String(status || "")
      .toUpperCase()
  ) {
    case "ACTIVE":
      return "Active";

    case "PENDING":
      return "Pending";

    case "EXPIRED":
      return "Expired";

    case "CANCELLED":
      return "Cancelled";

    case "FAILED":
      return "Failed";

    default:
      return status || "Unknown";
  }
};

export const getPaymentStatusLabel = (
  status
) => {
  switch (
    String(status || "")
      .toUpperCase()
  ) {
    case "PAID":
      return "Paid";

    case "PENDING":
      return "Pending";

    case "FAILED":
      return "Failed";

    case "REFUNDED":
      return "Refunded";

    default:
      return status || "Unknown";
  }
};


// =====================================================
// SUBSCRIPTION USAGE API
// =====================================================

const USAGE_API =
  "/api/subscription-usage";

export const getActiveFeatureUsage = (
  userId,
  userType,
  featureCode = "JOB_POST"
) => {
  if (!userId || !userType) {
    throw new Error("User ID and user type are required.");
  }

  const params = new URLSearchParams({
    userId: String(userId),
    userType: String(userType).toUpperCase(),
    featureCode: String(featureCode).toUpperCase(),
  });

  return request(`${USAGE_API}/active?${params.toString()}`);
};

export const checkFeatureUsage = (
  userId,
  userType,
  featureCode = "JOB_POST"
) => {
  const params = new URLSearchParams({
    userId: String(userId),
    userType: String(userType).toUpperCase(),
    featureCode: String(featureCode).toUpperCase(),
  });

  return request(`${USAGE_API}/check?${params.toString()}`);
};


// =====================================================
// GET ACTIVE SUBSCRIPTION + FEATURE USAGE
// =====================================================

export const getSubscriptionQuota = async (
  userId,
  userType,
  featureCode = "JOB_POST"
) => {
  if (!userId || !userType) {
    throw new Error("User ID and user type are required.");
  }

  const [subscription, usage] = await Promise.all([
    getActiveSubscription(userId),
    getActiveFeatureUsage(userId, userType, featureCode),
  ]);

  return {
    subscription,
    usage,
    allowedLimit: Number(usage?.allowedLimit ?? 0),
    usedCount: Number(usage?.usedCount ?? 0),
    remainingCount: Number(usage?.remainingCount ?? 0),
    limitReached: Boolean(usage?.limitReached),
  };
};